/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import CollectionPlaceholder from 'bundles/browse/components/CollectionPlaceholder';
import CollectionsCarousel from 'bundles/collections-carousel/components/carousel/CollectionsCarousel';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';

const styles = {
  carousel: css`
    .rc-BrowseCarousel .slick-slide img {
      width: unset;
      object-fit: cover;
      overflow: hidden;
    }

    @media (max-width: 375px) {
      contain: content;
    }

    .recommendation-modules-container {
      max-width: 100%;
    }
  `,
  legacyCarousel: css`
    .rc-BrowseCarousel .slick-slide .partner-logo-lohp-legacy img {
      object-fit: none;
      height: 25px;
      width: 25px;
    }
  `,
};

export const getAdditionalComponentsHashMap = (
  collectionsLength: number,
  additionalComponents?: ({ component: React.ReactNode; indexToInsert: number } | null)[]
) => {
  const map = new Map();

  if (additionalComponents && additionalComponents.length !== 0) {
    additionalComponents.forEach((additionalComponent) => {
      if (additionalComponent?.component) {
        let index = additionalComponent.indexToInsert;
        if (index >= collectionsLength) {
          index = collectionsLength - 1;
        }

        if (!map.get(index)) {
          map.set(index, [additionalComponent.component]);
        } else {
          const components = [...map.get(index), additionalComponent.component];
          map.set(index, components);
        }
      }
    });
  }

  return map;
};

const CollectionCarouselsContainer = ({
  collections,
  isCollectionsLoading,
  additionalComponents,
  isLegacyCollection,
  customResponsiveProperties,
}: {
  collections: CarouselCollection[];
  isCollectionsLoading?: boolean;
  additionalComponents?: ({ component: React.ReactNode; indexToInsert: number } | null)[];
  isLegacyCollection?: boolean;
  customResponsiveProperties?: {
    breakpoint: number;
    settings: { slidesToShow: number; slidesToScroll: number; infinite: boolean };
  }[];
}) => {
  const collectionsLength = collections.length;

  const additionalComponentsHashMap = useMemo(
    () => getAdditionalComponentsHashMap(collectionsLength, additionalComponents),
    [additionalComponents, collectionsLength]
  );

  if (!collections || collections.length === 0) return null;

  return (
    <div
      data-testid="CollectionsCarouselContainer"
      css={[styles.carousel, isLegacyCollection ? styles.legacyCarousel : {}]}
    >
      {!isCollectionsLoading ? (
        collections.map((collection, index) => {
          const extraComponents = additionalComponentsHashMap.get(index);
          if (extraComponents) {
            return (
              <>
                {extraComponents}
                <CollectionsCarousel
                  collection={collection}
                  collectionIndex={index}
                  key={collection.collectionId}
                  customResponsiveProperties={customResponsiveProperties}
                />
              </>
            );
          }

          return (
            <CollectionsCarousel
              collection={collection}
              collectionIndex={index}
              key={collection.collectionId}
              customResponsiveProperties={customResponsiveProperties}
            />
          );
        })
      ) : (
        <CollectionPlaceholder />
      )}
    </div>
  );
};

export default CollectionCarouselsContainer;
